import { Directive, inject } from '@angular/core';
import { AbstractControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { DialogOptions, Errore } from 'src/app/shared/models/dto';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { environment } from 'src/environments/environment';

@Directive()
export abstract class BaseComponent {

  constructor() {
    this._utilityService = inject(UtilityService);

    this.today = moment();
    this.tomorrow = moment().add(1, 'day');
    this.oneMonthBeforeToday = moment().subtract(1, 'month');
    this.oneWeekBeforeToday = moment().subtract(7, 'day');
    this.oneMonthFromToday = moment().add(1, 'month');
    this.startOfMonth = moment().set('date', 1);
    this.endOfMonth = moment().set('date', moment().daysInMonth());
    this.startOfYear = moment().set('date', 1).set('month', 0);
    this.oneYearFromToday = moment().add(1, 'year');

    this.isLocalhost = environment.environmentName == 'localhost';

    this.errorStateMatcher = new ErrorStateMatcher();

    this.errorStateMatcher.isErrorState = (control: AbstractControl | null, form: FormGroupDirective | NgForm | null) => {
      return control?.invalid;
    };

    this.oidErrorStateMatcher = new ErrorStateMatcher();

    this.oidErrorStateMatcher.isErrorState = (control: AbstractControl | null, form: FormGroupDirective | NgForm | null) => {
      let validator = control.validator({} as AbstractControl);

      if (validator && validator['required']) {
        return control.value == null || control?.value <= 0;
      }

      return false;
    };
  }

  private _utilityService: UtilityService;

  public today: moment.Moment;
  public tomorrow: moment.Moment;
  public startOfMonth: moment.Moment;
  public endOfMonth: moment.Moment;
  public oneMonthFromToday: moment.Moment;
  public oneYearFromToday: moment.Moment;
  public oneMonthBeforeToday: moment.Moment;
  public oneWeekBeforeToday: moment.Moment;
  public startOfYear: moment.Moment;

  public isLocalhost: boolean;

  public pageSizeOptions: number[] = [10, 25, 50, 100];
  public pageSize: number = 10;

  public errorStateMatcher: ErrorStateMatcher;
  public oidErrorStateMatcher: ErrorStateMatcher;

  public compareWithOid = (a: any, b: any) => a != null && b != null && a.oid == b.oid;

  public get lingue() {
    return this._utilityService.lingue;
  }

  getDate(value: any, format: string = 'YYYY-MM-DD'): string {
    return this._utilityService.getDate(value, format);
  }

  getEnumKeyTranslation(en: object, value: any, enumName?: string): string {
    return this._utilityService.getEnumKeyTranslation(en, value, enumName);
  }

  getEnumKeyOriginal(en: object, value: any): string {
    return this._utilityService.getEnumKeyOriginal(en, value);
  }

  getLocale(): string {
    return this._utilityService.getLocale();
  }

  setIsLoading(isLoading: boolean) {
    this._utilityService.setIsLoading(isLoading);
  }

  showErrors(errori: Errore[], title?: string): Promise<any> {
    return this._utilityService.showErrors(errori, title);
  }

  showError(idMessage: string, params?: any, title?: string): Promise<any> {
    return this._utilityService.showError(idMessage, params, title);
  }

  showSequentialMessages(messages: string[], isErrore: boolean) {
    return this._utilityService.showSequentialMessages(messages, isErrore);
  }

  showInfo(idMessage: string, params?: any, title?: string): Promise<any> {
    return this._utilityService.showInfo(idMessage, params, title);
  }

  showDialogConfirm(idMessage: string, params?: any): Promise<any> {
    return this._utilityService.showDialogConfirm(idMessage, params);
  }

  showModalDialog(componentRef: any, options: DialogOptions): MatDialogRef<{}, any> {
    return this._utilityService.showModalDialog(componentRef, options);
  }

  showSnackBar(message: string, duration: number = 2000) {
    return this._utilityService.showSnackBar(message, duration);
  }

  copyToClipboard(text: string) {
    return this._utilityService.copyToClipboard(text);
  }

  getCultureInfoName(lingua: string): string {
    return this.lingue.find(x => x.codice == lingua)?.cultureInfoName || null;
  }
}